import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Contact, Plus, Pencil, Trash2, Mail, Phone, FolderArchive, Download, X } from "lucide-react";
import { artUrl } from "@/lib/uploadArt";
import { tagClasses } from "@/lib/tags";
import { toast } from "sonner";

const EMPTY = { name: "", email: "", phone: "", notes: "" };

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [detail, setDetail] = useState(null);
  const [detailFiles, setDetailFiles] = useState([]);

  const load = () => api.get("/clients").then(({ data }) => setClients(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openDetail = (c) => {
    setDetail(c); setDetailFiles([]);
    api.get("/library/files", { params: { client_id: c.id } }).then(({ data }) => setDetailFiles(data)).catch(() => {});
  };

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, email: c.email || "", phone: c.phone || "", notes: c.notes || "" }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Client name is required"); return; }
    try {
      if (editing) await api.put(`/clients/${editing.id}`, form);
      else await api.post("/clients", form);
      toast.success(editing ? "Client updated" : "Client added");
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete client "${c.name}"? Their files will stay in the library but become unassigned.`)) return;
    try { await api.delete(`/clients/${c.id}`); toast.success("Client deleted"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="clients-page">
      <PageHeader eyebrow="Directory" title="Clients" subtitle="Your customer directory — files in the library attach to these clients." testid="clients-header">
        <Button onClick={openNew} data-testid="client-new-btn"><Plus size={16} className="mr-1.5" /> New client</Button>
      </PageHeader>

      <div className="p-8">
        {clients.length === 0 ? (
          <div className="text-center text-slate-400 py-20" data-testid="clients-empty">
            <Contact size={40} className="mx-auto mb-3 opacity-50" />
            No clients yet. Add your first client to start attaching files.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clients-grid">
            {clients.map((c) => (
              <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`client-card-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1 cursor-pointer" onClick={() => openDetail(c)} data-testid={`client-open-${c.id}`}>
                    <div className="font-semibold text-slate-800 truncate hover:text-[#2495D3]">{c.name}</div>
                    {c.email && <div className="text-xs text-slate-500 flex items-center gap-1 mt-1 truncate"><Mail size={12} /> {c.email}</div>}
                    {c.phone && <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><Phone size={12} /> {c.phone}</div>}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => openEdit(c)} className="text-slate-400 hover:text-[#2495D3] p-1" data-testid={`client-edit-${c.id}`}><Pencil size={15} /></button>
                    <button onClick={() => remove(c)} className="text-slate-400 hover:text-red-500 p-1" data-testid={`client-delete-${c.id}`}><Trash2 size={15} /></button>
                  </div>
                </div>
                {c.notes && <p className="text-xs text-slate-400 mt-2 line-clamp-2">{c.notes}</p>}
                <button onClick={() => openDetail(c)} className="mt-3 text-[11px] font-mono text-slate-500 hover:text-[#2495D3] flex items-center gap-1.5" data-testid={`client-files-${c.id}`}><FolderArchive size={13} /> {c.file_count || 0} file{(c.file_count || 0) === 1 ? "" : "s"} in library</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="client-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit client" : "New client"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Name *</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-form-name" /></div>
            <div className="flex gap-3">
              <div className="flex-1"><label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Email</label>
                <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-form-email" /></div>
              <div className="flex-1"><label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Phone</label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="client-form-phone" /></div>
            </div>
            <div><label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Notes</label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="client-form-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="client-form-save">{editing ? "Save" : "Add client"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client detail — files */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="client-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Contact size={18} /> {detail?.name}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-4 text-xs text-slate-500">
                {detail.email && <span className="flex items-center gap-1"><Mail size={12} /> {detail.email}</span>}
                {detail.phone && <span className="flex items-center gap-1"><Phone size={12} /> {detail.phone}</span>}
              </div>
              <div className="text-[11px] font-mono uppercase text-slate-400 pt-1">Files in library ({detailFiles.length})</div>
              {detailFiles.length === 0 ? (
                <div className="text-sm text-slate-400 py-6 text-center" data-testid="client-detail-empty">No files saved for this client yet.</div>
              ) : (
                <div className="space-y-1.5" data-testid="client-detail-files">
                  {detailFiles.map((f) => (
                    <div key={f.file_id} className="flex items-center justify-between gap-2 bg-slate-50 rounded-lg px-3 py-2 text-sm" data-testid={`client-file-${f.file_id}`}>
                      <div className="min-w-0">
                        <div className="font-medium text-slate-700 truncate">{f.filename}</div>
                        <div className="flex flex-wrap items-center gap-1 mt-0.5">
                          {(f.tags || []).map((t) => <span key={t} className={`text-[10px] px-2 py-0.5 rounded-full border ${tagClasses(t)}`}>{t}</span>)}
                          <span className="text-[10px] text-slate-400">{f.retention === "30day" ? "30 days" : f.retention === "permanent" ? "Permanent" : "Temporary"}</span>
                        </div>
                      </div>
                      <a href={artUrl(f.file_id)} target="_blank" rel="noreferrer" download className="text-slate-400 hover:text-[#2495D3] shrink-0" title="Download" data-testid={`client-file-download-${f.file_id}`}><Download size={15} /></a>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-slate-400 pt-1">Orders for this client will appear here once orders are linked to clients (coming soon).</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
